import { useCallback } from 'react'

import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useFormikContext } from 'formik'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { isAxiosError } from '@b-stock/fe-core/bstock-next/mutations'
import type {
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  DateTimeFormatType,
  ClientTimeZoneDateTime,
  FormattedMessage,
  PrecisionNumberFormat,
  parseOptionalISODate,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import type { OfferDto } from '@b-stock/offering-api-client'

import { GenericSaleProcessConfirmationModal } from '@/buyer-portal/components/features/listing/ListingActionColumn/GenericSaleProcessConfirmationModal'
import getMaxVatCents from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/getMaxVatCents'
import MaxVatExposureDisplay from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/MaxVatExposureDisplay'
import useEuMaxVatExposure from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/useEuMaxVatExposure'
import isEuTaxCurrency from '@/buyer-portal/helpers/euTaxCurrency'
import { createOffer } from '@/buyer-portal/mutations/offers/createOffer'
import { updateOffer } from '@/buyer-portal/mutations/offers/updateOffer'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { sendTypedGTMEvent } from '@/helpers/telemetry/sendTypedGTMEvent'
import { useGaSessionIdPayload } from '@/helpers/telemetry/useGaSessionIdPayload'
import type { GTMMaoSubmitEvent } from '@/helpers/types/gtmTypes'
import listingDetailsQuery from '@/queries/listingDetailsQuery'

import { useOfferAnalytics } from '../useOfferAnalytics'
import { useOfferTlc } from '../useOfferTlc'
import { type MakeAnOfferFormValues } from './MakeAnOfferFormProvider'

const ns = 'Listing.actionColumn.simpleOfferActionModule.confirmationModal'

export const MakeAnOfferConfirmationModal = ({
  listing,
  closeModal,
  offerId,
  selectedQuote,
}: {
  listing: OrchestratedOfferingListingDetails
  closeModal: () => void
  offerId?: string
  selectedQuote?: OrchestratedQuote | null
}) => {
  const { offering } = listing
  const { offerEndTime } = offering ?? {}
  const { values, resetForm } = useFormikContext<MakeAnOfferFormValues>()
  const { trackButtonClicked } = useAnalytics()
  const getOfferAnalytics = useOfferAnalytics(listing)
  const { bstockFee, totalLandedCostWithTax } = useOfferTlc(
    listing,
    selectedQuote
  )

  const contractUnit = listing.contract?.bidUnit
  const currency = listing.currency ?? 'USD'
  const isContract = listing.listingType === 'CONTRACT'

  // The Bid/Buy Now confirm modals already fold this in — this one never
  // got it, so its Estimated Total Payment and breakdown always omitted
  // EU/GB VAT.
  const isEuVatApplicable = isEuTaxCurrency(currency) && !isContract
  const maxVatData = useEuMaxVatExposure({
    buyerAddressId: selectedQuote?.destination.id,
    buyerFeeCents: bstockFee,
    formProxyMaxBid: values.amount ?? 0,
    formSingleBid: values.amount ?? 0,
    isTaxable: isEuVatApplicable,
    listingId: listing._id,
    shippingCents: selectedQuote?.totalPrice,
  })
  const totalTaxCents = getMaxVatCents(maxVatData)

  const formattedAmount = (
    <>
      <PrecisionNumberFormat
        value={values.amount}
        precision={listing.offering?.bidPrecision ?? 1}
        currency={currency}
        format={
          listing.contract?.bidBasis === 'RELATIVE' ? 'percent' : 'currency'
        }
      />
      {contractUnit && (
        <FormattedMessage id={`Contracts.Enums.perUnit.${contractUnit}`} />
      )}
    </>
  )

  const queryClient = useQueryClient()
  const accessToken = useAccessToken()
  // GLOB-4954: `{}` unless the kill switch is on AND the GA cookies are present.
  const getGaSessionIdPayload = useGaSessionIdPayload()

  const onSuccess = useCallback(
    ({ data }: { data: OfferDto }) => {
      queryClient.setQueryData(
        listingDetailsQuery.getKey(accessToken, [listing._id]),
        (oldListing: OrchestratedOfferingListingDetails) => {
          return {
            ...oldListing,
            offers: [data],
          }
        }
      )
      resetForm()
      closeModal()
      showSnackbar({
        content: <FormattedMessage id={`${ns}.successMessage`} />,
        options: { autoClose: true, type: 'success' },
      })
    },
    [closeModal, listing._id, accessToken, queryClient, resetForm]
  )

  const onError = useCallback(
    (error: unknown) => {
      const errorMessage = isAxiosError(error)
        ? (error.response?.data as { errorMessage?: string } | undefined)
            ?.errorMessage
        : undefined

      if (errorMessage === 'Offering has been completed') {
        showSnackbar({
          content: (
            <FormattedMessage
              id={`${ns}.error.acceptedEarly`}
              values={{
                date: (
                  <ClientTimeZoneDateTime
                    dateTime={parseOptionalISODate(offerEndTime)}
                    format={DateTimeFormatType.FULL}
                  />
                ),
              }}
            />
          ),
          options: { autoClose: true, type: 'error' },
        })
      } else if (
        errorMessage === 'Buyer status is not permitted to make an offer'
      ) {
        showSnackbar({
          content: <FormattedMessage id={`${ns}.error.suspendedBuyer`} />,
          options: { autoClose: true, type: 'error' },
        })
      } else {
        showSnackbar({
          content: <FormattedMessage id="Common.error.somethingWentWrong" />,
          options: { autoClose: true, type: 'error' },
        })
      }
    },
    [offerEndTime]
  )

  const { mutate: createOfferMutation, isPending: isCreatingOffer } =
    useMutation({
      mutationFn: createOffer,
      onSuccess,
      onError,
    })

  const { mutate: updateOfferMutation, isPending: isUpdatingOffer } =
    useMutation({
      mutationFn: updateOffer,
      onSuccess,
      onError,
    })

  const handleConfirm = useCallback(() => {
    const offerAmountCents = values.amount ?? 0
    const offerAmountDollars = offerAmountCents / 100
    const itemInput = orchestratedListingToGTMItemInput(listing)
    const maoSubmitEvent: GTMMaoSubmitEvent = {
      event: 'mao_submit',
      platform: GTM_PLATFORM,
      item_id: listing._id,
      offer_amount: offerAmountDollars,
      ecommerce: {
        currency,
        value: offerAmountDollars,
        items: [{ ...itemInput, index: 0, quantity: 1 }],
      },
    }
    sendTypedGTMEvent(maoSubmitEvent)

    trackButtonClicked({
      buttonName: 'confirm_offer',
      offer_amt_usd: offerAmountDollars,
      total_landed_cost_usd: totalLandedCostWithTax,
      ...getOfferAnalytics(),
    })

    const gaSessionIdPayload = getGaSessionIdPayload()

    if (offerId) {
      updateOfferMutation({
        accessToken: accessToken ?? '',
        offerId,
        replaceOfferDto: {
          offer: {
            lotAmount: values.amount ?? 0,
            quantities: [], // this should not be needed (remove once swagger fixed)
          },
        },
        gaSessionIdPayload,
      })
    } else {
      createOfferMutation({
        accessToken: accessToken ?? '',
        createOfferDto: {
          offeringId: listing.offering?._id ?? '',
          offer: {
            lotAmount: values.amount ?? 0,
            quantities: [], // this should not be needed (remove once swagger fixed)
          },
        },
        gaSessionIdPayload,
      })
    }
  }, [
    createOfferMutation,
    currency,
    getGaSessionIdPayload,
    getOfferAnalytics,
    listing,
    totalLandedCostWithTax,
    trackButtonClicked,
    values.amount,
    accessToken,
    offerId,
    updateOfferMutation,
  ])

  // note: dollar amount is only available and needed when absolute bid basis and not a contract, otherwise it's not used.
  const dollarAmount =
    listing.contract?.bidBasis === 'RELATIVE'
      ? null
      : (values.amount ?? 0) / 100

  return (
    <GenericSaleProcessConfirmationModal
      listing={listing}
      isLoading={isCreatingOffer || isUpdatingOffer}
      ns={ns}
      formattedAmount={formattedAmount}
      dollarAmount={dollarAmount}
      error={null}
      closeModal={closeModal}
      onConfirm={handleConfirm}
      selectedQuote={selectedQuote}
      totalTaxCents={totalTaxCents}
      vatDisplay={
        isEuVatApplicable && maxVatData ? (
          <MaxVatExposureDisplay data={maxVatData} currencyCode={currency} />
        ) : null
      }
    />
  )
}
